import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, Store, Calendar, Tag, ListChecks, Heart,
  AlertTriangle, Users, Building2, Menu, Receipt, Home as HomeIcon,
  HelpCircle, ChevronRight, X, Gift,
} from 'lucide-react';
import { useAuth } from '../App';
import { useFlags } from '../hooks/useFlags';
import NotificationBell from '../components/NotificationBell';
import LanguageToggle from '../components/LanguageToggle';
import BottomNavigation from '../components/BottomNavigation';

// One big, cheerful category button. White icon in a soft circle, large label.
const CategoryButton = ({ onClick, icon: Icon, label, desc, gradient, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`relative flex flex-col items-start justify-between rounded-3xl p-4 min-h-[128px] text-white shadow-lg bg-gradient-to-br ${gradient} active:scale-[0.97] transition-transform focus:outline-none focus:ring-4 focus:ring-white/40`}
  >
    <span className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-white/25 shadow-sm">
      <Icon className="h-7 w-7 drop-shadow" strokeWidth={2.4} />
    </span>
    <span className="mt-3 text-left">
      <span className="block text-[17px] font-black leading-tight">{label}</span>
      <span className="block text-[12px] font-medium text-white/90 leading-tight mt-0.5">{desc}</span>
    </span>
  </button>
);

export default function MobileDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useAuth();
  const flags = useFlags();
  const [q, setQ] = useState('');
  const [sheet, setSheet] = useState(null); // active category with sub-items

  useEffect(() => {
    if (!sheet) return;
    const onKey = (e) => { if (e.key === 'Escape') setSheet(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet]);

  const go = (path) => navigate(path);
  const submitSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/explore?q=${encodeURIComponent(q.trim())}` : '/explore');
  };

  // Business sub-items — affiliate link only when the programme is enabled.
  const businessItems = [
    { key: 'biz-dashboard', icon: Building2, label: t('home.bizDashboard', 'Business Dashboard'), desc: t('home.bizDashboardDesc', 'Manage your business'), to: '/business/dashboard' },
    { key: 'biz-join', icon: Store, label: t('home.bizJoin', 'List Your Business'), desc: t('home.bizJoinDesc', 'Start free'), to: '/business/signup' },
    { key: 'biz-guide', icon: HelpCircle, label: t('home.bizGuide', 'Memberships, Ratings & Badges'), desc: t('home.bizGuideDesc', 'How it all works'), to: '/business-guide' },
    ...(flags.affiliatesEnabled ? [{ key: 'biz-refer', icon: Gift, label: t('home.bizRefer', 'Refer & Earn'), desc: t('home.bizReferDesc', 'Community Partner programme'), to: '/affiliate' }] : []),
  ];

  // The six colourful categories. `to` navigates directly; `items` opens a sheet.
  const categories = [
    {
      id: 'shops', testId: 'home-cat-shops', icon: Store, gradient: 'from-emerald-500 to-green-600',
      label: t('home.catShops', 'Shops & Services'), desc: t('home.catShopsDesc', 'Find local businesses'),
      to: '/explore',
    },
    {
      id: 'prices', testId: 'home-cat-prices', icon: Receipt, gradient: 'from-sky-500 to-blue-600',
      label: t('home.catPrices', 'Compare Prices'), desc: t('home.catPricesDesc', 'Save money'),
      items: [
        { key: 'price', icon: Receipt, label: t('home.priceScan', 'Price-2-Day'), desc: t('home.priceScanDesc', 'Scan a receipt'), to: '/price' },
        { key: 'local', icon: HomeIcon, label: t('home.localPrices', 'Home & Local Prices'), desc: t('home.localPricesDesc', 'Fuel, firewood, DIY & more'), to: '/local' },
      ],
    },
    {
      id: 'whatson', testId: 'home-cat-whatson', icon: Calendar, gradient: 'from-pink-500 to-rose-500',
      label: t('home.catWhatson', "What's On"), desc: t('home.catWhatsonDesc', 'Events & offers'),
      items: [
        { key: 'events', icon: Calendar, label: t('home.events', 'Events'), desc: t('home.eventsDesc', "What's on near you"), to: '/events' },
        { key: 'offers', icon: Tag, label: t('home.offers', 'Offers'), desc: t('home.offersDesc', 'Deals near you'), to: '/offers' },
      ],
    },
    {
      id: 'organiser', testId: 'home-cat-organiser', icon: ListChecks, gradient: 'from-violet-500 to-purple-600',
      label: t('home.catOrganiser', 'My Organiser'), desc: t('home.catOrganiserDesc', 'Calendar & lists'),
      items: [
        { key: 'calendar', icon: Calendar, label: t('home.calendar', 'Calendar'), desc: t('home.calendarDesc', 'Plan your time'), to: '/calendar' },
        { key: 'lists', icon: ListChecks, label: t('home.lists', 'Lists'), desc: t('home.listsDesc', 'Stay organised'), to: '/calendar?view=lists' },
        { key: 'favourites', icon: Heart, label: t('home.favourites', 'Favourites'), desc: t('home.favouritesDesc', 'Save what you love'), to: '/favorites' },
      ],
    },
    {
      id: 'community', testId: 'home-cat-community', icon: Users, gradient: 'from-orange-500 to-amber-500',
      label: t('home.catCommunity', 'My Community'), desc: t('home.catCommunityDesc', 'Alerts & local info'),
      items: [
        { key: 'alerts', icon: AlertTriangle, label: t('home.alerts', 'Alerts'), desc: t('home.alertsDesc', 'Stay informed'), to: '/community-hub' },
        { key: 'localinfo', icon: Users, label: t('home.localInfo', 'Local Information'), desc: t('home.localInfoDesc', 'Your area guide'), to: '/followed-towns' },
      ],
    },
    {
      id: 'business', testId: 'home-cat-business', icon: Building2, gradient: 'from-teal-500 to-cyan-600',
      label: t('home.catBusiness', 'My Business'), desc: t('home.catBusinessDesc', 'Manage or join'),
      items: businessItems,
    },
  ];

  const onCategory = (cat) => {
    if (cat.to) go(cat.to);
    else setSheet(cat);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="mobile-dashboard">
      {/* HEADER */}
      <header className="bg-gradient-to-b from-[#132449] to-[#1e2f5c] text-white px-4 pt-5 pb-6 rounded-b-3xl shadow-lg">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/logo.jpg" alt="Find-2-Day" className="h-11 w-11 rounded-xl object-cover shrink-0 shadow" />
            <div className="min-w-0">
              <h1 className="text-2xl font-black leading-none">Find-2-Day</h1>
              <p className="text-[11px] text-white/75 leading-tight mt-1 truncate">
                {t('dash.tagline', 'Your local guide to businesses, events and community.')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <LanguageToggle />
            <NotificationBell />
            <button onClick={() => go('/profile')} data-testid="dash-menu" aria-label={t('nav.menu', 'Menu')}
              className="p-2 rounded-full hover:bg-white/15 transition-colors">
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
        <form onSubmit={submitSearch} className="mt-4">
          <div className="flex items-center gap-2 bg-white rounded-full px-4 py-3 shadow">
            <Search className="h-5 w-5 text-slate-400 shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('dash.searchPlaceholder', 'Search for businesses, events, offers and more…')}
              className="flex-1 bg-transparent outline-none text-slate-800 text-[15px] placeholder:text-slate-400"
              data-testid="dash-search-input"
              aria-label={t('dash.searchPlaceholder', 'Search')}
            />
          </div>
        </form>
      </header>

      {/* SIX CATEGORY BUTTONS */}
      <main className="px-4 -mt-2 max-w-3xl mx-auto">
        <div className="grid grid-cols-2 gap-3.5 mt-4" data-testid="home-category-grid">
          {categories.map((cat) => (
            <CategoryButton
              key={cat.id}
              testId={cat.testId}
              onClick={() => onCategory(cat)}
              icon={cat.icon}
              label={cat.label}
              desc={cat.desc}
              gradient={cat.gradient}
            />
          ))}
        </div>

        {/* Help & How It Works */}
        <div className="flex justify-center mt-5">
          <button
            onClick={() => go('/help')}
            data-testid="home-help-link"
            className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm font-semibold"
          >
            <HelpCircle className="h-4 w-4" />
            {t('home.help', 'Help & How It Works')}
          </button>
        </div>
      </main>

      {/* SUB-CATEGORY SHEET */}
      {sheet && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center"
          data-testid="home-cat-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={sheet.label}
          onClick={() => setSheet(null)}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
          <div
            className="relative w-full max-w-md bg-white rounded-t-3xl p-5 pb-28 shadow-2xl animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center h-9 w-9 rounded-xl bg-gradient-to-br ${sheet.gradient} text-white`}>
                  <sheet.icon className="h-5 w-5" />
                </span>
                <h2 className="text-lg font-black text-slate-800">{sheet.label}</h2>
              </div>
              <button onClick={() => setSheet(null)} data-testid="home-sheet-close" aria-label={t('close', 'Close')}
                className="p-2 rounded-full hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="space-y-2">
              {sheet.items.map((it) => (
                <button
                  key={it.key}
                  onClick={() => { setSheet(null); go(it.to); }}
                  data-testid={`home-link-${it.key}`}
                  className="w-full flex items-center gap-3 rounded-2xl border border-slate-200 p-3.5 text-left active:scale-[0.99] transition-transform hover:bg-slate-50"
                >
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-slate-100 text-slate-700 shrink-0">
                    <it.icon className="h-5 w-5" />
                  </span>
                  <span className="flex flex-col min-w-0 flex-1">
                    <span className="text-[15px] font-bold text-slate-800 leading-tight">{it.label}</span>
                    <span className="text-[12px] text-slate-500 leading-tight">{it.desc}</span>
                  </span>
                  <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
}
